import { ref, type Ref } from 'vue'
import type mapboxgl from 'mapbox-gl'
import { DRAW_STYLES } from '@/utils/mapbox/drawStyles'

// Maximum number of layer groups supported
// Z-layers are created in reverse order so z-0 is at the TOP
const MAX_LAYERS = 200
const Z_SOURCE = 'z-layers-empty'
const Z_FLOOR = 'z-floor'

const zLayerDef = (i: number) => ({
  id: `z-${i}`,
  type: 'fill' as const,
  source: Z_SOURCE,
  paint: { 'fill-opacity': 0 },
})

// Read the real insertion order: `style.order` is draped-first (fill/line/raster
// before circle/symbol) while terrain is enabled, which is not where addLayer
// actually inserts.
const layerOrder = (map: any): string[] =>
  map.style?._order ?? map.getStyle().layers.map((l: any) => l.id)

// Anchors are materialised on demand, so only the positions actually used
// exist. z-0 sits on top and a floor sentinel marks the bottom of the stack;
// every new (higher-numbered = lower-rendered) anchor goes directly above the
// floor, beneath any content already anchored to lower-numbered anchors.
export function ensureZLayer(map: mapboxgl.Map | null | undefined, n: number): string | undefined {
  if (!map || !(map as any).style || n < 0 || n >= MAX_LAYERS) return undefined
  if (!map.getSource(Z_SOURCE)) {
    map.addSource(Z_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
  }
  if (!map.getLayer(Z_FLOOR)) map.addLayer({ ...zLayerDef(0), id: Z_FLOOR })
  if (!map.getLayer('z-0')) map.addLayer(zLayerDef(0))
  for (let i = 1; i <= n; i++) {
    if (map.getLayer(`z-${i}`)) continue
    const order = layerOrder(map)
    map.addLayer(zLayerDef(i), order[order.indexOf(Z_FLOOR) + 1])
  }
  return `z-${n}`
}

// Per-instance init state, keyed by a stable instance key. The single global
// canvas uses 'global'; split-screen tiles pass their mapId so each Mapbox
// instance gets its own z-layer anchors initialized independently.
const initializedByKey = new Map<string, Ref<boolean>>()

function getInitializedRef(key: string): Ref<boolean> {
  let r = initializedByKey.get(key)
  if (!r) {
    r = ref(false)
    initializedByKey.set(key, r)
  }
  return r
}

// Drop a key's init state when its Mapbox instance is destroyed, so switching
// orgs / cycling many tiles doesn't retain refs forever.
export function releaseZLayers(key: string): void {
  initializedByKey.delete(key)
}

export function useZLayers(map: Ref<mapboxgl.Map | null>, instanceKey = 'global') {
  const initialized = getInitializedRef(instanceKey)

  /**
   * Initialize z-layers on map load
   * Creates empty reference layers that serve as stable anchor points
   * Z-layers are created in reverse order: z-49 first (bottom), z-0 last (top)
   * This ensures layer at position 0 (top of UI list) renders on top of map
   */
  function init(): void {
    if (!map.value || initialized.value) return
    ensureZLayer(map.value, 0)
    initialized.value = true
  }

  /**
   * Get the beforeId for a layer at the given position.
   * Position 0 = top of UI list = should render on TOP of map.
   *
   * In Mapbox, layers with a beforeId are inserted BEFORE that reference layer
   * in the rendering order, meaning they appear BELOW it visually.
   *
   * Since z-0 is at top and z-49 at bottom:
   * - Position 0 uses z-0 as beforeId, placing the layer immediately below z-0
   *   in the visual stack (appearing on top of the map content)
   * - Position 1 uses z-1 as beforeId, placing the layer below z-1
   * - etc.
   */
  function getBeforeId(position: number): string {
    // Clamp position to valid range
    const clampedPosition = Math.max(0, Math.min(position, MAX_LAYERS - 1))
    return `z-${clampedPosition}`
  }

  /**
   * Check if a layer ID is a z-layer (internal reference layer)
   */
  function isZLayer(layerId: string): boolean {
    return /^z-\d+$/.test(layerId)
  }

  return {
    init,
    getBeforeId,
    isZLayer,
    initialized,
    MAX_LAYERS
  }
}

/**
 * Mapbox-gl-draw injects layers named `<style-id>.cold` and `<style-id>.hot`.
 * We treat any layer with those suffixes as a draw layer.
 */
export function isDrawLayer(layerId: string): boolean {
  return layerId.endsWith('.cold') || layerId.endsWith('.hot')
}

/**
 * Move all mapbox-gl-draw layers to the top of the layer stack so they
 * always render above content. Calling moveLayer with no beforeId appends
 * the layer to the end of the stack (= top in render order).
 */
const DRAW_LAYER_IDS: string[] = [
  ...DRAW_STYLES.map((s: any) => `${s.id}.cold`),
  ...DRAW_STYLES.map((s: any) => `${s.id}.hot`),
]

export function bringDrawLayersToTop(map: mapboxgl.Map | null | undefined): void {
  // Skip while a style is mid-load (moveLayer would throw). MapDraw re-runs
  // this on the map's `style.load`, so nothing is permanently missed.
  if (!map || !(map as any).isStyleLoaded?.()) return
  for (const id of DRAW_LAYER_IDS) {
    if (!map.getLayer(id)) continue
    try {
      map.moveLayer(id)
    } catch (e) {
      console.warn(`[ZLayers] Failed to raise draw layer ${id}:`, e)
    }
  }
}
